import { getCharts } from '@/api/book';
import ChartTooltip from '@/components/bookStatus/ChartTooltip';
import { WEEKLY_AREA_LIST, WEEKLY_BAR_LIST } from '@/constants/book';
import { Icon } from '@successmode-ktp/kui';
import { add, addDays, format, getDay, sub } from 'date-fns';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';

type Props = {
  nations: string[];
  endDate: string;
  setEndDate: (date: string) => void;
};

function WeeklyChart({ endDate, setEndDate, nations }: Props) {
  const [first, second, third] = nations;

  const [chartData, setChartData] = useState<
    {
      name: string;
      total: number;
      first: number;
      second: number;
      third: number;
      etc: number;
    }[]
  >([]);
  const [domain, setDomain] = useState([0, 100]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({
    x: -9999,
    y: -9999,
  });

  const getNextSunday = (date: string) => {
    const dayOfWeek = getDay(new Date(date)); // 0(일) ~ 6(토)
    const daysUntilSunday = (7 - dayOfWeek) % 7 || 7; // 오늘이 일요일이면 다음 주 일요일
    return format(addDays(new Date(date), daysUntilSunday), 'yyyy-MM-dd');
  };

  const getPreviousSunday = (date: string) => {
    const dayOfWeek = getDay(new Date(date)); // 0(일) ~ 6(토)
    const daysUntilSunday = dayOfWeek === 0 ? 0 : dayOfWeek; // 오늘이 일요일이면 0, 아니면 일요일까지의 차이
    return format(addDays(new Date(date), -daysUntilSunday), 'yyyy-MM-dd'); // 지난 일요일 구하기
  };

  const getLastFiveWeeks = (date: string) =>
    Array.from({ length: 5 }, (_, index) => {
      const currentSunday = getPreviousSunday(date);
      const endDate = format(
        addDays(new Date(currentSunday), -7 * index),
        'yyyy-MM-dd',
      );
      const startDate = format(addDays(new Date(endDate), -6), 'yyyy-MM-dd');
      return { startDate, endDate };
    }).reverse();

  const sundayList = getLastFiveWeeks(getNextSunday(endDate));

  const { data } = useQuery(
    ['chartsData', nations, endDate],
    () =>
      getCharts({
        nations,
        interval: 'WEEKLY',
        endDate: getNextSunday(endDate),
      }),
    {
      onSuccess: (data) => {
        const newData = Object.entries(data.details).map(([date, counts]) => {
          return {
            name: format(new Date(date), 'M월 d째주'),
            total:
              (counts[first] || 0) +
              (counts[second] || 0) +
              (counts[third] || 0) +
              (counts['ETC'] || 0),
            first: counts[first] || 0,
            second: counts[second] || 0,
            third: counts[third] || 0,
            etc: counts['ETC'] || 0,
          };
        });
        setChartData(newData);

        const maxDataValue = Math.max(...newData.map((item) => item.total));
        const roundedMax = Math.ceil(maxDataValue / 10) * 10;
        setDomain([0, roundedMax || 100]);
      },
      keepPreviousData: true,
    },
  );

  const modifiedData2 = chartData.map((entry) => ({
    ...entry,
    area1: entry.total,
    area2: entry.second + entry.third + entry.etc,
    area3: entry.third + entry.etc,
    area4: entry.etc,
  }));

  const isPastEndDate = endDate !== format(new Date(), 'yyyy-MM-dd');

  const handleBarClick = (e: any, index: number) => {
    setActiveIndex(activeValue === e.name ? null : index);
    setActiveValue((prev) => (prev === e.name ? null : e.name));
    onChangeTooltipPos({
      tooltipX: e.tooltipPosition.x,
      tooltipY: e.tooltipPosition.y,
    });
  };

  const onChangeTooltipPos = ({
    tooltipX,
    tooltipY,
  }: {
    tooltipX: number;
    tooltipY: number;
  }) => {
    const y = tooltipY > 200 ? tooltipY - 150 : tooltipY;
    setTooltipPos({
      x: tooltipX - 50,
      y,
    });
  };

  useEffect(
    function resetActiveIndex() {
      if (endDate === format(new Date(), 'yyyy-MM-dd')) {
        setActiveIndex(chartData.length - 1);
        setActiveValue(chartData[chartData.length - 1]?.name);
      }
    },
    [endDate, chartData],
  );

  return (
    <Wrapper>
      <PageButton
        onClick={() =>
          setEndDate(
            format(sub(new Date(endDate), { months: 1 }), 'yyyy-MM-dd'),
          )
        }
      >
        <Icon.ChevronLeft size={18} fillColor='#949494' />
      </PageButton>
      <ResponsiveContainer width='100%' height={320}>
        <ComposedChart
          data={modifiedData2}
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 0,
          }}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <CartesianGrid stroke='#CBCCCE' vertical={false} />
          <XAxis
            dataKey='name'
            tickLine={false}
            axisLine={false}
            dy={10}
            tick={({ x, y, payload, index }) => {
              const isActive = index === activeIndex;
              return (
                <g transform={`translate(${x},${y + 20})`}>
                  <text
                    textAnchor='middle'
                    fill={isActive ? '#0A84FF' : '#80848A'}
                    style={{
                      fontSize: '14px',
                      fontWeight: isActive ? 'bold' : 'normal',
                    }}
                  >
                    {payload.value}
                  </text>
                  {sundayList[index]?.startDate &&
                    sundayList[index]?.endDate && (
                      <text
                        x={0}
                        y={18}
                        textAnchor='middle'
                        fill={isActive ? '#0A84FF' : '#80848A'}
                        style={{ fontSize: '12px' }}
                      >
                        {format(new Date(sundayList[index].startDate), 'M/d')}-
                        {''}
                        {format(new Date(sundayList[index].endDate), 'M/d')}
                      </text>
                    )}
                </g>
              );
            }}
          />
          <YAxis
            domain={domain}
            tickLine={false}
            axisLine={false}
            tickCount={3}
            tick={({ x, y, payload }) => {
              return (
                <text
                  x={x - 14}
                  y={y + 4}
                  textAnchor='right'
                  fill='#80848A'
                  style={{
                    fontSize: '12px',
                  }}
                >
                  {payload.value === 0 ? '0명' : payload.value}
                </text>
              );
            }}
          />
          {WEEKLY_AREA_LIST.map(({ dataKey, fill }) => (
            <Area
              key={dataKey}
              type='linear'
              dataKey={dataKey}
              fill={fill}
              stroke='transparent'
            />
          ))}

          {WEEKLY_BAR_LIST.map(
            ({
              dataKey,
              fill,
              radius = [0, 0, 0, 0],
              children,
            }: {
              dataKey: string;
              fill: string;
              radius?: any;
              children?: React.ReactNode;
            }) => (
              <Bar
                key={dataKey}
                dataKey={dataKey}
                stackId='a'
                barSize={48}
                fill={fill}
                radius={radius || [0, 0, 0, 0]}
                onClick={(e, index) => handleBarClick(e, index)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    cursor='pointer'
                    stroke={index === activeIndex ? '#18C4FF' : 'transparent'}
                    key={`cell-${index}`}
                  />
                ))}
                {children}
              </Bar>
            ),
          )}

          {/* 커스텀 툴팁 */}
          {activeValue !== null &&
            chartData.find((d) => d.name === activeValue) && (
              <foreignObject
                x={tooltipPos?.x}
                y={tooltipPos?.y}
                width={100}
                height={120}
              >
                <div {...{ xmlns: 'http://www.w3.org/1999/xhtml' }}>
                  <ChartTooltip
                    nations={nations}
                    data={chartData.find((d) => d.name === activeValue)}
                  />
                </div>
              </foreignObject>
            )}
        </ComposedChart>
      </ResponsiveContainer>
      <PageButton
        onClick={() =>
          setEndDate(
            format(add(new Date(endDate), { months: 1 }), 'yyyy-MM-dd'),
          )
        }
        disabled={!isPastEndDate}
      >
        <Icon.ChevronRight size={18} fillColor='#949494' />
      </PageButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbccce;
  border-radius: 100px;
  &:disabled {
    opacity: 0.25;
  }
`;

export default WeeklyChart;
